/**
 * Splitting a mode's answer into "the result" and "the notes".
 *
 * Some modes append a teaching section — Mistakes ends with its REWRITE and
 * PATTERN blocks. Rendering all of it in the output pane makes every
 * answer look long, and length is what makes a tool feel like homework: the
 * translation you asked for gets pushed up the screen by a glossary you did not.
 *
 * So anything from the first ALL-CAPS heading onward moves to its own panel
 * below the widget, where it is available without being in the way.
 *
 * The heading must not be the FIRST line, though. Modes like Mistakes open with
 * `ISSUES` — there the report *is* the result, and splitting it would leave the
 * output pane empty. That case deliberately falls through unchanged.
 */

/**
 * A section-marker line: all capitals, optionally led by an emoji.
 *
 * The leading-symbol strip is load-bearing. The prompts label sections with a
 * glyph ("💎 WORDS THAT CAN CONFUSE"), and a pattern anchored on `^[A-Z]` stops
 * matching the moment that glyph appears — the split would quietly do nothing
 * and the whole glossary would land back in the output pane.
 */
function isHeading(line: string): boolean {
  const bare = line.trim().replace(/^[^\p{L}]+/u, '').trim();
  return /^[A-Z][A-Z0-9 &'’/-]{2,}$/.test(bare);
}

export interface SplitOutput {
  /** What belongs in the output pane. */
  main: string;
  /** The trailing sections, or '' when there are none. */
  notes: string;
}

export function splitOutput(text: string): SplitOutput {
  if (!text) return { main: '', notes: '' };

  const lines = text.split('\n');
  const at = lines.findIndex(isHeading);

  // No heading, or the answer opens with one → nothing to move.
  if (at <= 0) return { main: text, notes: '' };

  // Several headings means the answer is a structured REPORT — Mistakes writes
  // ISSUES, REWRITE, PATTERN — and the whole report is the result. Splitting at
  // the first one leaves the pane holding whatever happened to come before it
  // and files the actual answer under a notes card. That was already the
  // intent (see the `at === 0` case above); it only held while the model
  // remembered to open with the heading, and it does not always.
  if (lines.filter(isHeading).length > 1) return { main: text, notes: '' };

  const main = lines.slice(0, at).join('\n').trim();
  const notes = lines.slice(at).join('\n').trim();

  // A heading with nothing above it is the same case as `at === 0`; guard so a
  // stray blank first line cannot empty the output pane.
  if (!main) return { main: text, notes: '' };

  return { main, notes };
}

/**
 * The tag the model puts in front of its one-sentence overview.
 *
 * Must stay identical to `OVERVIEW_TAG` in the backend's lib/modes.ts — that is
 * where it is written into the prompt, and this is where it is read back out.
 */
export const OVERVIEW_TAG = '[[OVERVIEW]]';

/**
 * One sentence, whatever the model actually sent.
 *
 * Both overview prompts ask for "ONE sentence of at most 25 words", and on a
 * long input the model ignores it — measured on a 685-character message, the
 * input-language card came back as five sentences and a bulleted task list,
 * roughly the whole message paraphrased. A summary the length of the thing it
 * summarises is worse than no summary: it pushes the actual text off the screen
 * and there is nothing to gain by reading it.
 *
 * So the prompt asks and this enforces. Prompts are a request; a parser is a
 * guarantee, and this one runs on the streamed text anyway.
 *
 * Cutting rules, each one from a way the naive version got it wrong:
 *
 *   • A terminator only ends a sentence when a space or the end of the string
 *     follows it — otherwise "3.5 tonnes", "e.g." and "sfs.com" all end the
 *     summary mid-thought.
 *   • CJK punctuation (。！？) ends a sentence on its own: Korean and Japanese
 *     do not put a space after it, so the space rule would never fire and the
 *     clamp would silently do nothing in exactly the languages this app is
 *     mostly used for.
 *   • No LATIN full stop inside the first 25 characters counts, which is
 *     shorter than any real summary and is where "Mr." and "Dr." live. The
 *     guard is deliberately not applied to 。！？ — 25 characters of Japanese is
 *     a whole sentence, and applying it there let "会議は火曜日の三時からです。
 *     ノートパソコンを持参してください。" through untouched.
 *   • A blob with no terminator at all still gets cut: at the first line break
 *     if there is one, because that shape is the model answering with a list,
 *     and otherwise on a word boundary with an ellipsis.
 */
const CJK_END = /[。！？]/;
const END = /[.!?…]/;

export function oneSentence(raw: string, maxChars = 200): string {
  const flat = raw.replace(/\s+/g, ' ').trim();
  if (!flat) return '';

  for (let i = 0; i < flat.length; i++) {
    const ch = flat[i];
    const cjk = CJK_END.test(ch);
    if (!cjk && !END.test(ch)) continue;
    const next = flat[i + 1];
    if (!cjk) {
      if (i + 1 < 25) continue;
      if (next !== undefined && next !== ' ') continue;
    }
    return flat.slice(0, i + 1);
  }

  // Unpunctuated, so fall back on shape. A line break in something that claims
  // to be one sentence means a list arrived; the first line is the nearest
  // thing to a summary in it.
  const firstLine = raw.trim().split('\n')[0].trim();
  const base = firstLine.length >= 25 ? firstLine : flat;

  if (base.length <= maxChars) return base;
  const cut = base.slice(0, maxChars);
  const space = cut.lastIndexOf(' ');
  return (space > maxChars * 0.6 ? cut.slice(0, space) : cut).trimEnd() + '…';
}

export interface SplitOverview {
  /** The answer itself, with the overview taken off the end. */
  body: string;
  /** The one-sentence summary, or '' when there is none. */
  overview: string;
}

/**
 * Take the overview off the end of an answer.
 *
 * The model writes it LAST — a summary of a translation cannot be composed
 * before the translation — while the interface shows it FIRST. This is the seam
 * between those two facts.
 *
 * The partial-tag loop is not defensive padding. This runs on every streamed
 * chunk, so the tag genuinely arrives a few characters at a time, and without it
 * the output pane visibly types out `[`, `[[`, `[[OVER` on the end of the
 * translation before the whole tag lands and it all disappears again.
 */
export function splitOverview(text: string): SplitOverview {
  if (!text) return { body: '', overview: '' };

  // Last, not first: if the model ever echoes the tag inside the answer, the
  // real overview is still the one at the end.
  const at = text.lastIndexOf(OVERVIEW_TAG);
  if (at >= 0) {
    return {
      body: text.slice(0, at).trimEnd(),
      // Clamped here rather than at the card, so every reader of this — the
      // widget, the admin replay, the stored transcript — sees the same one
      // sentence. See `oneSentence`.
      overview: oneSentence(text.slice(at + OVERVIEW_TAG.length)),
    };
  }

  for (let n = OVERVIEW_TAG.length - 1; n > 0; n--) {
    if (text.endsWith(OVERVIEW_TAG.slice(0, n))) {
      return { body: text.slice(0, text.length - n).trimEnd(), overview: '' };
    }
  }

  return { body: text, overview: '' };
}

/**
 * The tag in front of the learner notes — the words in the answer somebody at
 * A1/A2 would not know, explained.
 *
 * Must stay identical to `NOTES_TAG` in the backend's lib/modes.ts.
 */
export const NOTES_TAG = '[[NOTES]]';

/**
 * The overview again, in the language the user wrote in.
 *
 * Must stay identical to `SOURCE_OVERVIEW_TAG` in the backend's lib/modes.ts.
 *
 * The target-language overview belongs over the translation. This one belongs
 * over the BOX, because the box is the half of the screen written in a language
 * the reader is certain to read — and "did it understand what I said?" is a
 * question you want answered before you trust an answer you cannot check.
 *
 * It arrives LAST, after the target overview, so it must be taken off FIRST:
 * `splitOverview` keeps everything after its own tag, which would otherwise
 * swallow this whole line into the target card.
 */
export const SOURCE_OVERVIEW_TAG = '[[OVERVIEW_SRC]]';

export interface SplitSourceOverview {
  /** Everything before it — still carrying the answer, notes and overview. */
  body: string;
  /** The summary in the input language, or '' when the model was not asked. */
  sourceOverview: string;
}

export function splitSourceOverview(text: string): SplitSourceOverview {
  if (!text) return { body: '', sourceOverview: '' };

  const at = text.lastIndexOf(SOURCE_OVERVIEW_TAG);
  if (at >= 0) {
    return {
      body: text.slice(0, at).trimEnd(),
      sourceOverview: oneSentence(text.slice(at + SOURCE_OVERVIEW_TAG.length)),
    };
  }

  // The same half-arrived-tag guard the others need, and one it must not get
  // wrong: `[[OVERVIEW` is a complete prefix of BOTH tags, so trimming it here
  // while the rest is still on the wire is what stops the target overview
  // flashing on screen with `[[OVERVIEW_S` growing on its end.
  for (let n = SOURCE_OVERVIEW_TAG.length - 1; n > 0; n--) {
    if (text.endsWith(SOURCE_OVERVIEW_TAG.slice(0, n))) {
      return { body: text.slice(0, text.length - n).trimEnd(), sourceOverview: '' };
    }
  }

  return { body: text, sourceOverview: '' };
}

export interface SplitNotes {
  /** The answer itself, with the notes taken off the end. */
  body: string;
  /** The notes block, or '' when the model had nothing worth explaining. */
  notes: string;
}

/**
 * Take the notes off the end of an answer.
 *
 * Run this AFTER `splitOverview` — the overview is the very last thing in the
 * text, so taking the notes off first would carry it along with them.
 *
 * The partial-tag loop is the same one `splitOverview` needs and for the same
 * reason: this runs on every streamed chunk, and without it the answer visibly
 * grows `[`, `[[`, `[[NOT` on its end before the whole tag lands.
 */
export function splitNotes(text: string): SplitNotes {
  if (!text) return { body: '', notes: '' };

  const at = text.lastIndexOf(NOTES_TAG);
  if (at >= 0) {
    return {
      body: text.slice(0, at).trimEnd(),
      notes: text.slice(at + NOTES_TAG.length).trim(),
    };
  }

  for (let n = NOTES_TAG.length - 1; n > 0; n--) {
    if (text.endsWith(NOTES_TAG.slice(0, n))) {
      return { body: text.slice(0, text.length - n).trimEnd(), notes: '' };
    }
  }

  return { body: text, notes: '' };
}

/**
 * The Mistakes mode reports "your English is fine" with this exact sentinel, so
 * the UI can answer with one clear graphic instead of a paragraph saying the
 * same thing in words the reader gains nothing from.
 */
export const NO_MISTAKES = 'NO_MISTAKES';

/**
 * The Grammar Fix tab's two parts: the corrected text, and the short list of
 * serious fixes under a `🔧 FIXES` heading (see backend lib/modes.ts).
 *
 * The corrected text comes first and is what the reader copies; the list is
 * lifted into its own card so it never sits inside the thing being copied. The
 * heading is matched on the bare word FIXES after any leading symbol, so the
 * wrench is optional and a stray trailing space does not hide it. When the
 * model found nothing serious it writes no heading at all, and `fixes` is ''.
 *
 * Deliberately NOT `splitOutput`: a corrected prompt can legitimately contain
 * an ALL-CAPS line (`TODO`, `API`), and splitting on the first of those would
 * tear a real prompt in half. This splits on the one heading this tab emits.
 */
export interface GrammarFixSplit {
  body: string;
  fixes: string;
}

export function splitGrammarFixes(text: string): GrammarFixSplit {
  if (!text) return { body: '', fixes: '' };
  const lines = text.split('\n');
  const isFixesHeading = (line: string) => {
    const bare = line.trim().replace(/^[^\p{L}]+/u, '').trim();
    return /^FIXES$/i.test(bare);
  };
  const at = lines.findIndex(isFixesHeading);
  // Not the first line: a report with nothing above it has no corrected text to
  // show, which is never what this tab produces — treat it as still arriving.
  if (at <= 0) return { body: text, fixes: '' };
  return {
    body: lines.slice(0, at).join('\n').replace(/\s+$/, ''),
    fixes: lines.slice(at + 1).join('\n').trim(),
  };
}

/**
 * A flagged line: a real error, or correct-but-unnatural. The two marks the
 * Mistakes format uses, and the only proof that the report found anything.
 */
const FLAGGED = /[\u274C\u26A0]/;

export function isNoMistakes(text: string): boolean {
  const t = text.trim();
  if (t === NO_MISTAKES) return true;
  // The sentinel is supposed to arrive alone. It does not always: told there is
  // nothing to fix, the model sometimes prints the empty section skeleton first
  // and puts NO_MISTAKES underneath — which drew as a report with four headings
  // and nothing in them. The prompt now says not to; this is what makes the
  // interface right anyway.
  //
  // Only when nothing was actually flagged, though. If the answer contains real
  // findings AND the sentinel, the model has contradicted itself, and the safe
  // reading of "you have mistakes / you have none" is to show the mistakes.
  return t.includes(NO_MISTAKES) && !FLAGGED.test(t);
}

/**
 * Could this still BECOME the sentinel?
 *
 * True for the half-arrived word — `N`, `NO_MIS` — and for nothing yet at all.
 * The stream hands over a few characters at a time, and without this the pane
 * spends a frame or two claiming the translation of the text is `NO_MIS`, then
 * replaces it with a thumbs-up. One answer, shown once, is the point.
 *
 * Deliberately NOT true for the complete sentinel: that one is a result and is
 * drawn immediately.
 */
export function isNoMistakesPartial(text: string): boolean {
  const t = text.trim();
  return t.length < NO_MISTAKES.length && NO_MISTAKES.startsWith(t);
}

/**
 * The three registers of a Registers answer.
 *
 * Must stay identical to `FORMAL_TAG` / `FRIENDLY_TAG` / `CASUAL_TAG` in the
 * backend's lib/modes.ts. Tags rather than headings because that answer is
 * written in the user's own language: `isHeading` above is ALL-CAPS-Latin only
 * and finds nothing at all in Korean or Chinese, which would leave the versions
 * welded into one paragraph with no visible seam.
 */
export const FORMAL_TAG = '[[FORMAL]]';
export const FRIENDLY_TAG = '[[FRIENDLY]]';
export const CASUAL_TAG = '[[CASUAL]]';

export interface Variant {
  key: 'formal' | 'friendly' | 'casual';
  text: string;
}

/** Presentation order, which is also register order — most formal first. The
 *  model is asked for them in this order, but a card is drawn wherever its tag
 *  lands, so nothing downstream depends on the model getting that right. */
export const VARIANT_ORDER: Variant['key'][] = ['formal', 'friendly', 'casual'];

/**
 * Trim a tag that is still arriving off the end of a stream.
 *
 * The same problem `splitOverview` solves, and the same fix: chunks land a few
 * characters at a time, so without this the pane visibly types `[`, `[[`,
 * `[[CAS` onto the end of the formal version a beat before the casual card
 * appears. Every tag this file knows about is checked, because a partial `[[N`
 * could become either `[[NOTES]]` or nothing.
 *
 * The cost is that an answer whose last character really is `[` renders one
 * chunk late. That is the whole cost.
 */
function stripPartialTag(text: string): string {
  for (const tag of [FORMAL_TAG, FRIENDLY_TAG, CASUAL_TAG, OVERVIEW_TAG, SOURCE_OVERVIEW_TAG, NOTES_TAG]) {
    for (let n = tag.length - 1; n > 0; n--) {
      if (text.endsWith(tag.slice(0, n))) return text.slice(0, text.length - n).trimEnd();
    }
  }
  return text;
}

const VARIANT_TAG = /\[\[(FORMAL|FRIENDLY|CASUAL)\]\]/g;

/**
 * Is this answer the multi-card kind, and if so what is in each card?
 *
 * Returns `null` for every ordinary answer — one pane of text — so the caller
 * can treat "no tags" and "not that mode" as the same thing and never has to ask
 * which mode produced the string it is holding.
 *
 * An empty array is a real answer too: it means the first tag has landed but the
 * text under it has not. Drawing an empty card for that instant would be a box
 * flashing on screen with nothing in it, so callers show the pending state
 * instead — which is what was already there.
 */
export function splitVariants(text: string): Variant[] | null {
  if (!text) return null;
  const body = stripPartialTag(text);
  if (!body.includes(FORMAL_TAG) && !body.includes(FRIENDLY_TAG) && !body.includes(CASUAL_TAG)) {
    return null;
  }

  const out: Variant[] = [];
  let key: Variant['key'] | null = null;
  let at = 0;
  VARIANT_TAG.lastIndex = 0;

  for (let m = VARIANT_TAG.exec(body); m; m = VARIANT_TAG.exec(body)) {
    if (key) out.push({ key, text: body.slice(at, m.index).trim() });
    key = m[1].toLowerCase() as Variant['key'];
    at = m.index + m[0].length;
  }
  if (key) out.push({ key, text: body.slice(at).trim() });

  // A card with nothing in it is either mid-stream or a tag the model wrote
  // twice; neither is worth a box on screen.
  return out.filter((v) => !!v.text);
}

/* ── AI Prompt styles ──────────────────────────────────────────────────────*/

/**
 * The AI Prompt mode answers once per STYLE, and the pane draws the styles as
 * tabs. Each version is introduced by a tag naming the style it belongs to:
 *
 *     [[STYLE:original]]  …the prompt…  [[STYLE:developing]]  …the prompt…
 *
 * Unlike the register tags above, the set is not fixed: styles live in the
 * database and an operator adds and edits them from the console, so the id is
 * matched by shape rather than against a list. The shape is the same one the
 * backend validates ids with (see models/PromptStyle) — lowercase letters,
 * digits and hyphens — which is what keeps a stray `[[` in someone's prompt
 * from being read as a tab.
 */
const STYLE_TAG = /\[\[STYLE:([a-z0-9][a-z0-9-]{0,30})\]\]/g;

export interface StyleAnswer {
  styleId: string;
  text: string;
}

/**
 * Trim a style tag that is still arriving.
 *
 * `stripPartialTag` above cannot do this one: it works by testing the text
 * against every tag it knows, and these tags are not known in advance. So the
 * tail is examined instead — an unclosed `[[` that could still become a style
 * tag is held back until the rest of it lands. Without this the reader watches
 * `[`, `[[S`, `[[STYLE:dev` type themselves onto the end of the first tab.
 */
function stripPartialStyleTag(text: string): string {
  const at = text.lastIndexOf('[[');
  if (at === -1) return text;
  const tail = text.slice(at);
  if (tail.includes(']]')) return text;
  if ('[[STYLE:'.startsWith(tail) || /^\[\[STYLE:[a-z0-9-]*$/.test(tail)) {
    return text.slice(0, at).trimEnd();
  }
  return text;
}

/**
 * The tabbed answer, or null for every other mode.
 *
 * Returns the versions in the order the model wrote them, which is the order
 * the styles were given to it — so the first tab is the first style, and that
 * is the one the pane opens on.
 *
 * Text before the first tag is dropped rather than kept as an untitled tab. It
 * is either nothing at all (the normal case) or a preamble the model was told
 * not to write, and showing it would give that mistake a tab of its own.
 */
export function splitStyles(text: string): StyleAnswer[] | null {
  if (!text) return null;
  const body = stripPartialStyleTag(text);
  if (!body.includes('[[STYLE:')) return null;

  const out: StyleAnswer[] = [];
  let styleId: string | null = null;
  let at = 0;
  STYLE_TAG.lastIndex = 0;

  for (let m = STYLE_TAG.exec(body); m; m = STYLE_TAG.exec(body)) {
    if (styleId) out.push({ styleId, text: body.slice(at, m.index).trim() });
    styleId = m[1];
    at = m.index + m[0].length;
  }
  if (styleId) out.push({ styleId, text: body.slice(at).trim() });

  // A tab with nothing under it is mid-stream, not an answer.
  return out.filter((s) => !!s.text);
}
